
import { defineComponent, computed, onMounted, ref, onUnmounted } from "vue";
import HistoryList from "@/components/favorites/HistoryList.vue";
import MyList from "@/components/favorites/MyList.vue";
import {
  FavoriteRecipeClips,
  FolderInfo,
  FavoriteFolders,
  FavoriteHistoryRecipes,
  FavoriteSendedHistoryRecipes
} from "@/types/favorites";
import CommonDialog from "@/components/common/CommonDialog.vue";
import CommonOKDialog from "@/components/common/CommonOKDialog.vue";
import FavoritesFolderNameInputDialog from "@/components/favorites/FavoritesFolderNameInputDialog.vue";
import NativeClient, { TitleInfo } from "@/client/native-client";
import store from "@/store";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";
import PullToRefresh from "@/components/common/PullToRefresh.vue";

export default defineComponent({
  name: "FavoritesPage",
  components: { MyList, HistoryList, FavoritesFolderNameInputDialog, CommonDialog, CommonOKDialog, PullToRefresh },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(true, ""));
    CommonUtil.scroll({ top: 0, left: 0 });

    // テキスト定義
    const historiesTitle = "履歴";
    const mylistTitle = "マイリスト";

    // ダイアログ表示の制御
    class FavoritesPageDialogType {
      type: number; // 0:ダイアログ表示なし、1:フォルダ名設定ダイアログ、2:フォルダ数上限オーバー・メッセージ、3:フォルダ名文字数上限オーバー、
      constructor(type: number) {
        this.type = type;
      }
    }
    const refDialog = ref(new FavoritesPageDialogType(0));

    class FavoritesPageUpdateInfo {
      myList: boolean;
      folderList: boolean;
      sendedHistory: boolean;
      viewedHistory: boolean;

      constructor() {
        this.myList = false;
        this.folderList = false;
        this.sendedHistory = false;
        this.viewedHistory = false;
      }
    }

    // WebAPIのリクエスト状況
    class FavoritesPageRequestType {
      type: number; // 0:なし、1:一覧更新（どの一覧を更新するかは、updateInfo参照）、2:フォルダー追加、3:フォルダ並べ替え
      folderName: string; // フォルダ追加時フォルダ名
      replaceInfo: { folderId: number, position: number };
      updateInfo: FavoritesPageUpdateInfo;
      constructor(type: number, folderName: string, folderId: number, position: number, updateInfo: FavoritesPageUpdateInfo) {
        this.type = type;
        this.folderName = folderName;
        this.replaceInfo = { folderId: folderId, position: position };
        this.updateInfo = updateInfo;
      }
    }
    const refRequestType = ref(new FavoritesPageRequestType(0, "", 0, 0, new FavoritesPageUpdateInfo()));

    // 履歴/マイリスト洗濯状態の変数
    const selectedTab = {
      get value() {
        return store.getters["favorites/getSelectedTab"];
      },
      set value(tabIndex: number) {
        store.dispatch("favorites/updateSelectedTab", tabIndex);
      }
    };

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // フォルダ名入力のためのリアクティブ
    const updateInputFolderNameKey = ref(0);
    const updateInputFolderName = () => {
      if (updateInputFolderNameKey.value == 100) {
        updateInputFolderNameKey.value = 0;
      } else {
        updateInputFolderNameKey.value++;
      }
    };

    // ログIDの設定
    if (selectedTab.value == 0) {
      setLogId(PAGE_ID.FAVORITES_HISTORIES);
    } else {
      setLogId(PAGE_ID.FAVORITES_MYLIST);
    }

    //
    // API実行
    //

    // マイリストとフォルダ一覧
    const getMyList = async (valid: boolean) => {
      NativeClient.sendDebugLog("FavoritesPage updateMyList()");
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("favorites/getRecentMyList")
          ]).then((result) => {
            NativeClient.sendDebugLog("FavoritesPage getMyList() complete");
            resolve();
          });
        } else {
          NativeClient.sendDebugLog("FavoritesPage getMyList() no update");
          resolve();
        }
      });
      return promise;
    };

    // マイリストとフォルダ一覧
    const getFolderList = async (valid: boolean) => {
      NativeClient.sendDebugLog("FavoritesPage getFolderList()");
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("favorites/getFolderList")
          ]).then((result) => {
            NativeClient.sendDebugLog("FavoritesPage getFolderList() complete");
            resolve();
          });
        } else {
          NativeClient.sendDebugLog("FavoritesPage getFolderList() no update");
          resolve();
        }
      });
      return promise;
    };

    // 送信履歴取得
    const getSendedList = async (valid: boolean) => {
      NativeClient.sendDebugLog("FavoritesPage getSendedList()");
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("favorites/getTopSendedHistory")
          ]).then((result) => {
            NativeClient.sendDebugLog("FavoritesPage getSendedList() complete");
            resolve();
          });
        } else {
          NativeClient.sendDebugLog("FavoritesPage getSendedList() no update");
          resolve();
        }
      });
      return promise;
    };

    // 閲覧履歴取得
    const getViewedList = async (valid: boolean) => {
      NativeClient.sendDebugLog("FavoritesPage getViewedList()");
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("favorites/getTopViewedHistory")
          ]).then((result) => {
            NativeClient.sendDebugLog("FavoritesPage getViewedList() complete");
            resolve();
          });
        } else {
          NativeClient.sendDebugLog("FavoritesPage getViewedList() no update");
          resolve();
        }
      });
      return promise;
    };

    // フォルダのドラッグによるフォルダの順番入れ替え
    const moveFolder = async (folderId: number, position: number) => {
      store.dispatch("favorites/updateMoveFolder", null);
      NativeClient.sendDebugLog("FavoritesPage replace folder folderId = " + folderId + ", position = " + position);

      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        // フォルダの置き換えAPI
        refRequestType.value = new FavoritesPageRequestType(3, "", folderId, position, new FavoritesPageUpdateInfo());
        await store.dispatch("favorites/moveFolder", { folderId: folderId, position: position });

        // フォルダ一覧取得API
        const updateInfo = new FavoritesPageUpdateInfo();
        updateInfo.myList = false;
        updateInfo.folderList = true;
        updateInfo.sendedHistory = false;
        updateInfo.viewedHistory = false;
        refRequestType.value = new FavoritesPageRequestType(1, "", 0, 0, updateInfo);
        await getFolderList(true);
        updateDisplay();
      }
      store.dispatch("common/stopLoading");
    };

    // ビーコンログ出力
    const sendBeaconLog = () => {
      const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, null, []);
      if (selectedTab.value == 0) {
        logInfo.pageId = PAGE_ID.FAVORITES_HISTORIES;
      }
      NativeClient.sendViewedLog(logInfo);
    }

    // 一覧更新
    const updatePageList = async (info: FavoritesPageUpdateInfo) => {
      NativeClient.sendDebugLog("FavoritesPage updatePageList()");
      refRequestType.value = new FavoritesPageRequestType(1, "", 0, 0, info);
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await Promise.all([
        getMyList(info.myList),
        getFolderList(info.folderList),
        getSendedList(info.sendedHistory),
        getViewedList(info.viewedHistory)
      ]);
      store.dispatch("common/stopLoading");
      updateDisplay();
    }

    // 一覧更新（Nativeのインターフェースから）
    const updatePage = async (info: UpdatePageInfo) => {
      NativeClient.sendDebugLog("FavoritesPage updatePage()");
      const updateInfo = new FavoritesPageUpdateInfo();
      updateInfo.myList = info.myList;
      updateInfo.folderList = info.myList;
      updateInfo.sendedHistory = info.sendedHistory;
      updateInfo.viewedHistory = info.viewedHistory
      updatePageList(updateInfo);
    };

    // 実行
    const execute = async () => {
      NativeClient.sendDebugLog("FavoritesPage execute()");
      const updateInfo = new FavoritesPageUpdateInfo();
      updateInfo.myList = true;
      updateInfo.folderList = true;
      updateInfo.sendedHistory = true;
      updateInfo.viewedHistory = true;
      updatePageList(updateInfo);
    };

    // 表示の制御
    let isDisplayed = false;
    onMounted(() => {
      NativeClient.sendDebugLog("FavoritesPage onMounted()");
      isDisplayed = true;

      // マイリスト更新 
      NativeClient.registUpdatePageListener(updatePage);

      execute();

      sendBeaconLog();
    });
    onUnmounted(() => {
      isDisplayed = false;
    });

    // 選択ボタンのハンドラ
    const selectHistories = () => {
      // 履歴選択
      if (selectedTab.value != 0) {
        NativeClient.sendDebugLog("FavoritesPage select to Histories button");
        setLogId(PAGE_ID.FAVORITES_HISTORIES);
        selectedTab.value = 0;
        sendBeaconLog();
      }
    };
    const selectMylist = () => {
      // マイリスト選択
      if (selectedTab.value != 1) {
        NativeClient.sendDebugLog("FavoritesPage select to Mylist button");
        setLogId(PAGE_ID.FAVORITES_MYLIST);
        selectedTab.value = 1;
        sendBeaconLog();
      }
    };

    const styleOpacitySelectedHistories = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage select Histories button in styleOpacitySelectedHistories");
      if (selectedTab.value == 0) {
        NativeClient.sendDebugLog("FavoritesPage select to Histories in styleOpacitySelectedHistories");
        return { opacity: 1.0 };
      }
      return { opacity: 0.0 };
    });
    const styleOpacitySelectedMylist = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage select Favorites button in styleOpacitySelectedMylist");
      if (selectedTab.value == 1) {
        NativeClient.sendDebugLog("FavoritesPage select to Mylist in styleOpacitySelectedMylist");
        return { opacity: 1.0 };
      }
      return { opacity: 0.0 };
    });
    const styleTextColorSelectedHistories = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage select Histories button in styleTextColorSelectedHistories");
      if (selectedTab.value == 0) {
        NativeClient.sendDebugLog("FavoritesPage select to Histories");
        return { color: `#000000` };
      }
      return  { color: `#707070`};
    });
    const styleTextColorSelectedMylist = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage select Favorites button in styleTextColorSelectedMylist");
      if (selectedTab.value == 1) {
        NativeClient.sendDebugLog("FavoritesPage select to Mylist");
        return { color: `#000000` };
      }
      return { color: `#707070` };
    });
    const styleHistoriesArea = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage set view Histories Area in styleHistoriesArea");
      if (selectedTab.value == 0) {
        NativeClient.sendDebugLog("FavoritesPage set view Histories Area");
        return { zIndex: '5' };
      }
      return { zIndex: '4' };
    });
    const styleFavoritesArea = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage set view Favorites Area in styleFavoritesArea");
      if (selectedTab.value == 1) {
        NativeClient.sendDebugLog("FavoritesPage set view Favorites Area");
        return { zIndex: '5' };
      }
      return  { zIndex: '4' };
    });

    // フォルダ一覧
    let numFolders = 0;
    const folderList = computed(() => {
      const result: FavoriteFolders = store.getters["favorites/getFolderList"];
      if (result == null || result.folders == null) {
        NativeClient.sendDebugLog("FavoritesPage folderList null");
        return null;
      }
      const folderList: FolderInfo[] = [];
      for (const folderElement of result.folders) {
        const element = new FolderInfo();
        element.id = folderElement.favoriteFolderId;
        element.name = folderElement.name;
        element.image = folderElement.imageUrl;
        element.numberOfItems = folderElement.clipCount;
        folderList.push(element);
      }
      numFolders = folderList.length;
      return folderList;
    });

    // マイリスト
    const recipeList = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage befor getters mylist ");
      const result: FavoriteRecipeClips = store.getters["favorites/getRecentMyList"];
      NativeClient.sendDebugLog("FavoritesPage after getters mylist ");
      if (result == null || result.clips == null) {
        NativeClient.sendDebugLog("FavoritesPage mylist null");
        return null;
      }
      return result.clips;
    });

    // 送信履歴
    const sendedList = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage befor getters sendedList ");
      const result: FavoriteSendedHistoryRecipes = store.getters["favorites/getSendedHistory"];
      NativeClient.sendDebugLog("FavoritesPage after getters sendedList ");
      if (result == null || result.recipes == null) {
        NativeClient.sendDebugLog("FavoritesPage sendedList null");
        return null;
      }
      return result.recipes;
    });

    // 送信履歴
    const viewedList = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage befor getters viewedList ");
      const result: FavoriteHistoryRecipes = store.getters["favorites/getViewedHistory"];
      NativeClient.sendDebugLog("FavoritesPage after getters viewedList ");
      if (result == null || result.recipes == null) {
        NativeClient.sendDebugLog("FavoritesPage viewedList null");
        return null;
      }
      return result.recipes;
    });

    // フォルダ追加ボタン
    const appendFolderRequest = () => {
      if (numFolders < COMMON_LIMIT_NUM.FAVORITES_FOLDER_LIMIT) {
        refDialog.value = new FavoritesPageDialogType(1);
        updateInputFolderName();
      } else {
        // 上限値オーバー
        refDialog.value = new FavoritesPageDialogType(2);
      }
    };

    // フォルダー追加ダイアログ
    const showAppendFolderDialog = computed(() => {
      if (refDialog.value.type == 1) {
        // Beacon Log
        const logInfo = new BeaconLogInfo(PAGE_ID.DIALOG, null, ["お気に入りフォルダ追加"]);
        NativeClient.sendViewedLog(logInfo);

        return true;
      }
      return false;
    });
    const closeAppendFolderDialog = () => {
      NativeClient.sendDebugLog("FavoritesPage close append folder");
      refDialog.value = new FavoritesPageDialogType(0);
    }

    // フォルダー追加
    const appendFolder = async (folderName: string) => {
      refDialog.value = new FavoritesPageDialogType(0);
      store.dispatch("favorites/updateRegistFolder", null);
      NativeClient.sendDebugLog("FavoritesPage append folder = " + folderName);
      if (folderName.length > COMMON_LIMIT_NUM.FAVORITES_FOLDER_NAME_LIMIT) {
        // フォルダ名上限超え
        refDialog.value = new FavoritesPageDialogType(3);
        return;
      }

      // フォルダ追加API実行
      store.dispatch("common/startLoading");
      refRequestType.value = new FavoritesPageRequestType(2, folderName, 0, 0, new FavoritesPageUpdateInfo());
      await store.dispatch("favorites/registFolder", folderName);

      // フォルダ一覧取得API
      const updateInfo = new FavoritesPageUpdateInfo();
      updateInfo.myList = false;
      updateInfo.folderList = true;
      updateInfo.sendedHistory = false;
      updateInfo.viewedHistory = false;
      refRequestType.value = new FavoritesPageRequestType(1, folderName, 0, 0, updateInfo);
      await getFolderList(true);

      updateDisplay();
      store.dispatch("common/stopLoading");
    };

    // メッセージ・ダイアログ
    const refOKDialogMessage = ref("");
    const showOKDialog = computed(() => {
      const type = refDialog.value.type;
      let retValue = false;
      switch (type) {
        case 2:
          retValue = true;
          refOKDialogMessage.value = COMMON_MESSAGE_STRING.FAVORITES_FOLDER_LIMIT_OVER;
          break;
        case 3:
          retValue = true;
          refOKDialogMessage.value = COMMON_MESSAGE_STRING.FAVORITES_FOLDER_NAME_LIMIT_OVER;
          break;
        default:
          break;
      }
      return retValue;
    });
    const okDialogMessage = computed(() => {
      return refOKDialogMessage.value;
    });
    const okDialogHandler = () => {
      // 閉じる
      refDialog.value = new FavoritesPageDialogType(0);
    };

    // APIリクエスト時のエラーハンドル
    const {
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const showApiErrorDialog = computed(() => {
      const apiError: ApiError = store.getters["common/getApiError"];
      NativeClient.sendDebugLog("FavoritesPage showApiError");
      if (apiError == null) {
        NativeClient.sendDebugLog("FavoritesPage showApiError no error");
        return false;
      } else if (!isDisplayed) {
        NativeClient.sendDebugLog("FavoritesPage showApiError isDisplayed is false");
        return false;
      }
      return true;
    });
    const cancelHandler = () => {
      closeDialog();
      refRequestType.value = new FavoritesPageRequestType(0, "", 0, 0, new FavoritesPageUpdateInfo());
    };
    const retryHandler = () => {
      closeDialog();
      switch (refRequestType.value.type) {
        case 1:
          updatePageList(refRequestType.value.updateInfo);
          break;
        case 2:
          appendFolder(refRequestType.value.folderName);
          break;
        case 3:
          moveFolder(refRequestType.value.replaceInfo.folderId, refRequestType.value.replaceInfo.position);
          break;
        default:
          break;
      }
    };

    // マイリストのPull To Refresh
    const refreshMyList = () => {
      NativeClient.sendDebugLog("FavoritesPage refreshMyList");
      const updateInfo = new FavoritesPageUpdateInfo();
      updateInfo.myList = true;
      updateInfo.folderList = true;
      updateInfo.sendedHistory = false;
      updateInfo.viewedHistory = false;
      updatePageList(updateInfo);
    };

    // 履歴のPull To Refresh
    const refreshHistory = () => {
      NativeClient.sendDebugLog("FavoritesPage refreshHistory");
      const updateInfo = new FavoritesPageUpdateInfo();
      updateInfo.myList = false;
      updateInfo.folderList = false;
      updateInfo.sendedHistory = true;
      updateInfo.viewedHistory = true;
      updatePageList(updateInfo);
    };

    return {
      historiesTitle,
      mylistTitle,
      selectHistories,
      selectMylist,
      styleOpacitySelectedHistories,
      styleOpacitySelectedMylist,
      styleTextColorSelectedHistories,
      styleTextColorSelectedMylist,
      styleHistoriesArea,
      styleFavoritesArea,
      recipeList,
      folderList,
      sendedList,
      viewedList,
      appendFolderRequest,
      showAppendFolderDialog,
      closeAppendFolderDialog,
      appendFolder,
      moveFolder,
      showApiErrorDialog,
      getApiErrorTitle,
      getApiErrorMessage,
      cancelHandler,
      retryHandler,
      showOKDialog,
      okDialogMessage,
      okDialogHandler,
      updateInputFolderNameKey,
      updateDisplayKey,
      refreshMyList,
      refreshHistory
    };
  }
});
